import {
  Button,
  Collapse,
  Divider,
  Form,
  Input,
  InputRef,
  Select,
  Spin,
  Tooltip,
} from "antd"
import { useRef, useState } from "react"
import upArrowIcon from "assets/svg/chevron-up.svg"
import bluePlus from "assets/svg/plus-blue.svg"
import pencilIcon from "assets/svg/pencil.svg"
import deleteIcon from "assets/svg/trash.svg"
import { InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import {
  normalizeTimeInput,
  SHIFT_DIFFERENTIAL_HINT,
  YesNoToggle,
} from "../setup/components/CreateShiftPlanPeriods"
import { useGet } from "utils/useFetch"
import {
  IDraftShiftDataDocsEntity,
  IShiftDifferentialsRes,
  IShiftPeriodsRes,
  IShiftPlanDoc,
} from "../typings"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import CreateShiftPlanTables from "../setup/components/CreateShiftPlanTables"
import { toast } from "react-toastify"

// ─── Constants ────────────────────────────────────────────────────────────────

const MERIDIEM_OPTIONS = [
  { value: "am", label: "AM" },
  { value: "pm", label: "PM" },
]

const endSlotIcon =
  "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/49ee8442-4666-4558-bdc4-977b22b24c64.svg"

const EmptyState = () => (
  <img
    src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/eb351147-1557-4d64-a3f4-560b8b7adea8.png"
    alt="No shift periods"
  />
)
// ─── Types ────────────────────────────────────────────────────────────────────

type TimeValues = {
  name: string
  startHour: string
  startMin: string
  startMeridiem: "am" | "pm"
  endHour: string
  endMin: string
  endMeridiem: "am" | "pm"
  shiftDifferentialIds: number[]
}

type ModalState = { onCreate: boolean; onEdit: boolean }

// ─── Component ────────────────────────────────────────────────────────────────

const ShiftPeriodsAndAssignment = ({
  shiftPlanId,
  dutyLocationId,
  shiftPlan,
}: {
  shiftPlanId: number | undefined
  dutyLocationId: number | "workplace"
  shiftPlan: IShiftPlanDoc | IDraftShiftDataDocsEntity | undefined
}) => {
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const startHourRef = useRef<InputRef>(null)
  const startMinuteRef = useRef<InputRef>(null)
  const endHourRef = useRef<InputRef>(null)
  const endMinuteRef = useRef<InputRef>(null)

  const [selectedShiftPeriod, setSelectedShiftPeriod] = useState<number>()
  const [modal, setModal] = useState<ModalState>({
    onCreate: false,
    onEdit: false,
  })

  const queryKey = `/v1/time-attendance/shift-periods?page=1&limit=20&shiftPlanId=${shiftPlanId}`

  const { data: shiftDifferentialsRes } = useGet<IShiftDifferentialsRes>({
    url: "/v1/time-attendance/shift-differentials?page=1&limit=100",
    instanceKey: "ewaAxiosInstance",
    enabled: modal.onCreate || modal.onEdit,
  })
  const shiftDifferentials = (shiftDifferentialsRes?.data?.docs ?? []).filter(
    (d) => d.isActive
  )
  const differentialOptions = shiftDifferentials.map((d) => ({
    label: d.name,
    value: d.id,
  }))
  const differentialTitleById = new Map(
    shiftDifferentials.map((d) => [d.id, d.name])
  )

  const applyDifferentials = Form.useWatch("applyDifferentials", form)
  const selectedDifferentialIds: number[] =
    Form.useWatch("shiftDifferentialIds", form) || []

  const invalidateQueries = () =>
    queryClient.invalidateQueries({
      predicate: (q) => {
        const key = q.queryKey?.[0]
        return (
          typeof key === "string" &&
          (key.includes("shift-periods") || key.includes("shift-tables"))
        )
      },
    })

  const closeModal = () => {
    setModal({ onCreate: false, onEdit: false })
    form.resetFields()
  }

  // ─── Data fetching ──────────────────────────────────────────────────────────

  const { data: shiftPeriodsRes, isLoading: isShiftPeriodsLoading } =
    useGet<IShiftPeriodsRes>({
      url: queryKey,
      instanceKey: "ewaAxiosInstance",
      enabled: !!shiftPlanId,
    })

  // ─── Mutations ──────────────────────────────────────────────────────────────

  const { mutate: createShiftPeriod, isLoading: isCreating } = useMutation({
    mutationFn: (values: TimeValues) =>
      ewaAxiosInstance.post("/v1/time-attendance/shift-periods", {
        title: values.name,
        startTime: to24HourTime(
          values.startHour,
          values.startMin,
          values.startMeridiem
        ),
        endTime: to24HourTime(
          values.endHour,
          values.endMin,
          values.endMeridiem
        ),
        shiftPlanId: shiftPlanId?.toString(),
        status: "active",
        shiftDifferentialIds: values.shiftDifferentialIds,
      }),
    onSuccess: () => {
      invalidateQueries()
      toast.success("Shift period created successfully")
      closeModal()
    },
    onError: onQueryError,
  })

  const { mutate: updateShiftPeriod, isLoading: isUpdating } = useMutation({
    mutationFn: ({ id, values }: { id: string; values: TimeValues }) =>
      ewaAxiosInstance.put(`/v1/time-attendance/shift-periods/${id}`, {
        title: values.name,
        startTime: to24HourTime(
          values.startHour,
          values.startMin,
          values.startMeridiem
        ),
        endTime: to24HourTime(
          values.endHour,
          values.endMin,
          values.endMeridiem
        ),
        shiftPlanId: shiftPlanId?.toString(),
        status: "active",
        shiftDifferentialIds: values.shiftDifferentialIds,
      }),
    onSuccess: () => {
      invalidateQueries()
      toast.success("Shift period updated successfully")
      closeModal()
    },
    onError: onQueryError,
  })

  const {
    mutate: deleteShiftPeriod,
    isLoading: isDeleting,
    variables,
  } = useMutation({
    mutationFn: (shiftPeriodId: string) =>
      ewaAxiosInstance.delete(
        `/v1/time-attendance/shift-periods/${shiftPeriodId}`
      ),
    onSuccess: () => {
      invalidateQueries()
      toast.success("Shift period deleted successfully")
      closeModal()
    },
    onError: onQueryError,
  })

  // ─── Form submit ────────────────────────────────────────────────────────────

  const handleFinish = (values: any) => {
    const timeValues: TimeValues = {
      name: values.periodName,
      startHour: values.startHour,
      startMin: values.startMinute,
      startMeridiem: values.startMeridiem,
      endHour: values.endHour,
      endMin: values.endMinute,
      endMeridiem: values.endMeridiem,
      shiftDifferentialIds:
        values.applyDifferentials === "yes"
          ? values.shiftDifferentialIds ?? []
          : [],
    }

    if (modal.onEdit) {
      updateShiftPeriod({
        id: selectedShiftPeriod?.toString() || "",
        values: timeValues,
      })
    } else {
      createShiftPeriod(timeValues)
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Collapse
        defaultActiveKey={["1", "2"]}
        expandIcon={() => null}
        items={[
          {
            key: "1",
            className: "[&_.ant-collapse-content-box]:!p-0",
            label: <CollapseLabel title="Shift periods" />,
            children:
              isShiftPeriodsLoading || isUpdating ? (
                <Spin>
                  <div className="h-[94px] w-full bg-white rounded-lg border" />
                </Spin>
              ) : (
                <>
                  {shiftPeriodsRes?.data?.docs?.length ? (
                    <div className="grid grid-cols-2 gap-px bg-slate-200">
                      {shiftPeriodsRes?.data?.docs?.map((period) => (
                        <ShiftPeriodCard
                          key={period.id}
                          id={period.id}
                          name={period.title}
                          startTime={period.startTime}
                          endTime={period.endTime}
                          differentialCount={
                            period.shiftDifferentials?.length ?? 0
                          }
                          onEdit={() => {
                            const start = parse24HourTimeToForm(
                              period.startTime
                            )
                            const end = parse24HourTimeToForm(period.endTime)
                            const diffIds =
                              period.shiftDifferentials?.map((d) => d.id) ?? []
                            form.setFieldsValue({
                              periodName: period.title,
                              startHour: start.hour,
                              startMinute: start.minute,
                              startMeridiem: start.meridiem,
                              endHour: end.hour,
                              endMinute: end.minute,
                              endMeridiem: end.meridiem,
                              applyDifferentials: diffIds.length ? "yes" : "no",
                              shiftDifferentialIds: diffIds,
                            })
                            setSelectedShiftPeriod(period.id)
                            setModal({ onCreate: false, onEdit: true })
                          }}
                          onDelete={() =>
                            deleteShiftPeriod(period.id.toString())
                          }
                          isDeleting={
                            isDeleting && variables === period.id.toString()
                          }
                        />
                      ))}
                      {shiftPeriodsRes.data.docs.length % 2 === 1 && (
                        <div className="bg-white" />
                      )}
                    </div>
                  ) : (
                    <div className="p-4">
                      <EmptyState />
                    </div>
                  )}
                  <div className="p-3 border-t border-slate-200">
                    <Button
                      className="border-2 border-[#004AF5] text-[#004AF5] rounded-md h-10 flex items-center gap-2"
                      onClick={() => {
                        form.resetFields()
                        setModal({ onCreate: true, onEdit: false })
                      }}
                    >
                      <img
                        src={bluePlus}
                        width={15}
                        height={15}
                      />
                      Create shift period
                    </Button>
                  </div>
                </>
              ),
          },
          {
            key: "2",
            label: <CollapseLabel title="Employee assignment tables" />,
            children: (
              <CreateShiftPlanTables
                shiftPlanName={shiftPlan?.title}
                shiftType={
                  shiftPlan?.type === "rostered" ? "rostered" : "anytime"
                }
                shiftPlanId={shiftPlanId}
                dutyLocationId={
                  dutyLocationId === "workplace" ? "workplace" : dutyLocationId
                }
              />
            ),
          },
        ]}
      />

      {/* ── Create / Edit modal ── */}
      <CustomModal
        rootClassName="ta-scope"
        open={modal.onCreate || modal.onEdit}
        onCancel={closeModal}
        title={modal.onEdit ? "Edit shift period" : "Create shift period"}
        width={536}
        centered
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ applyDifferentials: "no", shiftDifferentialIds: [] }}
          onFinish={handleFinish}
          className="[&_.ant-form-item]:!mb-0"
        >
          <div className="flex flex-col gap-5">
            <Form.Item
              name="periodName"
              label={
                <span className="font-bold text-sm text-[#0F172B]">
                  Shift period's name
                </span>
              }
              rules={[
                { required: true, message: "Please enter a shift period name" },
              ]}
            >
              <Input
                placeholder="Give this shift period a name e.g morning shift"
                className="h-10 rounded-[6px]"
              />
            </Form.Item>

            <div className="flex items-end gap-3">
              <TimePicker
                label="Start time"
                hourName="startHour"
                minuteName="startMinute"
                meridiemName="startMeridiem"
                hourRef={startHourRef}
                minuteRef={startMinuteRef}
                onHourComplete={() => startMinuteRef.current?.focus()}
                onMinuteBackspace={() => startHourRef.current?.focus()}
              />

              <span className="text-[#94A3B8] flex items-end pb-2.5">—</span>

              <TimePicker
                label="End time"
                hourName="endHour"
                minuteName="endMinute"
                meridiemName="endMeridiem"
                hourRef={endHourRef}
                minuteRef={endMinuteRef}
                onHourComplete={() => endMinuteRef.current?.focus()}
                onMinuteBackspace={() => endHourRef.current?.focus()}
              />
            </div>

            {/* Apply shift differentials */}
            <div className="flex items-center gap-3 pt-1 border-t border-[#E2E8F0]">
              <span className="flex items-center gap-1.5 font-bold text-sm text-[#0F172B] pt-4">
                Apply shift differentials
                <Tooltip title={SHIFT_DIFFERENTIAL_HINT}>
                  <span
                    tabIndex={0}
                    role="img"
                    aria-label={SHIFT_DIFFERENTIAL_HINT}
                    className="inline-flex text-[#64748B] cursor-help"
                  >
                    <InformationCircleIcon className="w-4 h-4" />
                  </span>
                </Tooltip>
              </span>
              <Form.Item
                name="applyDifferentials"
                className="!mb-0 pt-4"
              >
                <YesNoToggle />
              </Form.Item>
            </div>

            {applyDifferentials === "yes" && (
              <div className="flex flex-col gap-3 border-l-2 border-[#E2E8F0] pl-4">
                <span className="font-bold text-sm text-[#0F172B]">
                  Shift differentials
                </span>
                <Form.Item
                  name="shiftDifferentialIds"
                  className="!mb-0"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select differentials to add"
                    options={differentialOptions}
                    className="w-full"
                    tagRender={() => <></>}
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
                {selectedDifferentialIds.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedDifferentialIds.map((id) => (
                      <span
                        key={id}
                        className="inline-flex items-center gap-2 rounded-md border border-[#E2E8F0] px-3 py-1.5 text-sm text-[#0F172B]"
                      >
                        {differentialTitleById.get(id) ?? id}
                        <button
                          type="button"
                          onClick={() =>
                            form.setFieldValue(
                              "shiftDifferentialIds",
                              selectedDifferentialIds.filter((x) => x !== id)
                            )
                          }
                        >
                          <XMarkIcon className="w-4 h-4 text-[#64748B]" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <Form.Item className="mt-5">
            <Button
              type="primary"
              className="w-full font-semibold h-11 rounded-lg bg-[#004AF5] text-white"
              onClick={() => form.submit()}
              loading={isCreating || isUpdating}
            >
              {modal.onEdit ? "Update shift period" : "Save shift period"}
            </Button>
          </Form.Item>
        </Form>
      </CustomModal>
    </>
  )
}

export default ShiftPeriodsAndAssignment

// ─── Sub-components ───────────────────────────────────────────────────────────

const CollapseLabel = ({ title }: { title: string }) => (
  <div className="flex justify-between items-center">
    <ul className="items-center bg-[#F8FAFC] text-[#62748E] font-semibold">
      <li>{title}</li>
    </ul>
    <img
      src={upArrowIcon}
      width={15}
      height={15}
    />
  </div>
)

type TimePickerProps = {
  label: string
  hourName: string
  minuteName: string
  meridiemName?: string
  hourRef: React.RefObject<InputRef>
  minuteRef: React.RefObject<InputRef>
  onHourComplete: () => void
  onMinuteBackspace: () => void
}

export const TimePicker = ({
  label,
  hourName,
  minuteName,
  meridiemName,
  hourRef,
  minuteRef,
  onHourComplete,
  onMinuteBackspace,
}: TimePickerProps) => (
  <div className="flex flex-col gap-1.5">
    <span className="font-semibold text-sm text-slate-900">{label}</span>
    <div className="flex items-stretch gap-1">
      <Form.Item
        name={hourName}
        noStyle
        rules={[
          { required: true, message: "" },
          {
            validator: (_, value: string) => {
              const n = Number(value)
              if (!value) return Promise.resolve()
              if (Number.isNaN(n) || n < 1 || n > 12) return Promise.reject("")
              return Promise.resolve()
            },
          },
        ]}
        normalize={normalizeTimeInput}
      >
        <Input
          ref={hourRef}
          placeholder="00"
          maxLength={2}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10)
            if (e.target.value.length === 2 && value >= 1 && value <= 12)
              onHourComplete()
          }}
        />
      </Form.Item>
      <span className="text-[#0F172B] font-semibold flex items-center">:</span>
      <Form.Item
        name={minuteName}
        noStyle
        rules={[
          { required: true, message: "" },
          {
            validator: (_, value: string) => {
              const n = Number(value)
              if (!value) return Promise.resolve()
              if (Number.isNaN(n) || n < 0 || n > 59) return Promise.reject("")
              return Promise.resolve()
            },
          },
        ]}
        normalize={normalizeTimeInput}
      >
        <Input
          ref={minuteRef}
          placeholder="00"
          maxLength={2}
          onKeyDown={(e) => {
            if (
              e.key === "Backspace" &&
              !(e.target as HTMLInputElement).value
            ) {
              onMinuteBackspace()
            }
          }}
        />
      </Form.Item>
      {meridiemName ? (
        <Form.Item
          name={meridiemName}
          noStyle
          rules={[{ required: true, message: "" }]}
          initialValue="am"
        >
          <Select
            className="min-w-[84px]"
            options={MERIDIEM_OPTIONS}
          />
        </Form.Item>
      ) : null}
    </div>
  </div>
)

type ShiftPeriodCardProps = {
  id: number | string
  name: string
  startTime: string
  endTime: string
  onEdit: () => void
  onDelete: () => void
  isDeleting: boolean
  differentialCount?: number
}

const formatPeriodDuration = (startTime: string, endTime: string): string => {
  const [startH, startM] = startTime.split(":").map(Number)
  const [endH, endM] = endTime.split(":").map(Number)
  const startSec = (startH || 0) * 3600 + (startM || 0) * 60
  const endSec = (endH || 0) * 3600 + (endM || 0) * 60
  const seconds =
    endSec >= startSec ? endSec - startSec : 86400 - startSec + endSec
  const units: [number, string, string][] = [
    [86400, "day", "days"],
    [3600, "hr", "hrs"],
    [60, "min", "mins"],
    [1, "sec", "secs"],
  ]
  const matched = units.find(([s]) => seconds >= s && seconds % s === 0)
  if (!matched) return `${seconds} secs`
  const value = seconds / matched[0]
  return `${value} ${value === 1 ? matched[1] : matched[2]}`
}

export const ShiftPeriodCard = ({
  id,
  name,
  startTime,
  endTime,
  onEdit,
  onDelete,
  isDeleting,
  differentialCount = 0,
}: ShiftPeriodCardProps) => {
  const start = format24HourTimeForDisplay(startTime)
  const end = format24HourTimeForDisplay(endTime)
  const duration = formatPeriodDuration(startTime, endTime)

  const handleCopyId = async () => {
    if (id == null) return
    try {
      await navigator.clipboard.writeText(String(id))
      toast.success("Shift period ID copied")
    } catch {
      toast.error("Failed to copy")
    }
  }

  return (
    <div className="bg-white p-4 flex flex-col gap-2 font-circular h-full">
      {/* Shift period's name */}
      <div className="flex flex-col gap-0.5">
        <p className="text-slate-500 text-xs m-0">Shift period's name</p>
        <p className="font-bold text-sm text-slate-900 m-0">{name}</p>
      </div>

      {/* Period time + Shift period ID side-by-side */}
      <div className="flex items-center gap-4">
        <div className="flex flex-col gap-0.5">
          <p className="text-slate-500 text-xs m-0">Period time</p>
          <p className="font-bold text-sm text-slate-900 m-0">
            {start} to {end} [{duration}]
          </p>
        </div>

        {id != null && (
          <>
            <div className="border-l border-slate-300 h-10" />
            <div className="flex flex-col gap-0.5">
              <p className="text-slate-500 text-xs m-0">Shift period ID</p>
              <span className="flex items-center gap-1.5">
                <p className="font-bold text-sm text-slate-900 m-0">{id}</p>
                <button
                  type="button"
                  onClick={handleCopyId}
                  className="text-slate-500 hover:text-slate-900"
                  aria-label="Copy shift period ID"
                >
                  <img
                    src={endSlotIcon}
                    width={14}
                    height={14}
                    className="w-3.5 h-3.5"
                    alt=""
                  />
                </button>
              </span>
            </div>
          </>
        )}

        {/* Differentials */}

        <>
          <div className="border-l border-slate-300 h-10" />
          <div className="flex flex-col gap-0.5">
            <p className="text-slate-500 text-xs m-0">Differentials</p>
            <p className="font-bold text-sm text-slate-900 m-0">
              {differentialCount} added
            </p>
          </div>
        </>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
        <Button
          className="border border-slate-200 rounded-md h-9 flex items-center gap-2 font-semibold text-slate-900 text-sm"
          onClick={onEdit}
        >
          <img
            src={pencilIcon}
            width={14}
            height={14}
            alt=""
          />
          Edit
        </Button>
        <Button
          className="border border-slate-200 rounded-md h-9 flex items-center gap-2 font-semibold text-slate-900 text-sm"
          onClick={onDelete}
          loading={isDeleting}
        >
          {!isDeleting && (
            <img
              src={deleteIcon}
              width={14}
              height={14}
              alt=""
            />
          )}
          Delete
        </Button>
      </div>
    </div>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseTimeToMinutes = (time: string): number | null => {
  const [hourStr, minuteStr] = time.split(":")
  const hour = Number(hourStr)
  const minute = Number(minuteStr)

  if (
    !hourStr ||
    !minuteStr ||
    Number.isNaN(hour) ||
    Number.isNaN(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  )
    return null

  return hour * 60 + minute
}

export const calculateShiftHours = (
  startTime: string,
  endTime: string
): number => {
  const start = parseTimeToMinutes(startTime)
  const end = parseTimeToMinutes(endTime)

  if (start === null || end === null) return 0

  const diff = end >= start ? end - start : 24 * 60 - start + end
  return diff / 60
}

function parse24HourTime(
  time: string
): { hour: number; minute: number } | null {
  const [hourStr, minuteStr] = String(time || "").split(":")
  const hour = Number(hourStr)
  const minute = Number(minuteStr)
  if (
    !hourStr ||
    !minuteStr ||
    Number.isNaN(hour) ||
    Number.isNaN(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  )
    return null
  return { hour, minute }
}

function format24HourTimeForDisplay(time: string): string {
  const parsed = parse24HourTime(time)
  if (!parsed) return time
  const { hour, minute } = parsed
  const meridiem = hour >= 12 ? "PM" : "AM"
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  return `${hour12}:${String(minute).padStart(2, "0")} ${meridiem}`
}

function parse24HourTimeToForm(time: string): {
  hour: string
  minute: string
  meridiem: "am" | "pm"
} {
  const parsed = parse24HourTime(time)
  if (!parsed) return { hour: "12", minute: "00", meridiem: "am" }
  const meridiem: "am" | "pm" = parsed.hour >= 12 ? "pm" : "am"
  const hour12 = parsed.hour % 12 === 0 ? 12 : parsed.hour % 12
  return {
    hour: String(hour12).padStart(2, "0"),
    minute: String(parsed.minute).padStart(2, "0"),
    meridiem,
  }
}

function to24HourTime(
  hour: string,
  minute: string,
  meridiem: "am" | "pm"
): string {
  const h = Number(hour)
  const m = Number(minute)
  const safeHour = Number.isNaN(h) ? 12 : Math.min(12, Math.max(1, h))
  const safeMinute = Number.isNaN(m) ? 0 : Math.min(59, Math.max(0, m))
  const h24 =
    meridiem === "am"
      ? safeHour === 12
        ? 0
        : safeHour
      : safeHour === 12
      ? 12
      : safeHour + 12
  return `${String(h24).padStart(2, "0")}:${String(safeMinute).padStart(
    2,
    "0"
  )}`
}
